/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * NO DASHES IN WHAT THE OFFICER OR CLIENT READS (v1_813).
 *
 * The product's own copy carries no hyphens or dashes (v1_784, v1_808). The
 * model's text does: findings, risk factors, the AI review, board narratives,
 * the client type, Alex's messages ("one-page", "retained-profits", "FCA-
 * authorised", "Confirms ... — consistent with the applicant name"). The walk's
 * copy check found them on 32 screens (walk w0b9f4a76, 25/09/26).
 *
 * plainText cleans a string FOR DISPLAY ONLY, at the point it is rendered. It is
 * never applied to stored text or to text logic reads (the case state splits
 * the source of funds score on a dash, for one). The rule mirrors the walk's
 * copy check (tools/walk/lib.mjs dashHits):
 *   an en or em dash, with its spacing          becomes ", " (or nothing leading)
 *   a spaced hyphen between words (" - ")        becomes ", "
 *   a hyphen joining two letters ("two-tier")   becomes a space ("two tier")
 * and it keeps what is data, not prose: URLs, email addresses, UUIDs and hex
 * ids, capital letter codes (CID-RM-1.3, HRB-12345, SMF-16), and hyphens
 * between digits (dates, ranges, sort codes).
 */
const PROTECT = [
  /https?:\/\/[^\s)]+/g,
  /[A-Za-z0-9._%+]+(?:-[A-Za-z0-9._%+]+)*@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
  /\b[A-Z][A-Z0-9]{1,}(?:-[A-Z0-9][A-Z0-9.]*)+\b/g,
  // Owner (31/10/2026): a Sumsub level name is an identifier, kept exactly
  // ("on level docs-no-liveness", "level aml-screen-only").
  /\blevel [A-Za-z0-9_]+(?:-[A-Za-z0-9_]+)+/g,
];

export function plainText(input) {
  if (input == null) return input;
  if (typeof input !== 'string') return input;
  if (!/[–—-]/.test(input)) return input;
  const kept = [];
  let s = input;
  for (const re of PROTECT) s = s.replace(re, (m) => { kept.push(m); return `\u0000${kept.length - 1}\u0000`; });
  s = s
    // A dash opening a line or the text is a bullet: drop it. v1_921: a hyphen
    // bullet too ("- Register of members" on the client's checklist).
    .replace(/(^|\n)[ \t]*[–—][ \t]*/g, '$1')
    .replace(/(^|\n)[ \t]*-[ \t]+/g, '$1')
    // A dash between words, with or without spaces, reads as a comma.
    .replace(/[ \t]*[–—][ \t]*/g, ', ')
    .replace(/(\S) - (?=\S)/g, '$1, ')
    // A hyphen joining two letters is a space ("one-page" is "one page").
    .replace(/([A-Za-zÀ-ɏ])-(?=[A-Za-zÀ-ɏ])/g, '$1 ')
    // Tidy what the replacements can leave.
    .replace(/,\s*,/g, ',')
    .replace(/,\s*([.;:)])/g, '$1')
    .replace(/\(\s*,\s*/g, '(')
    .replace(/[ \t]{2,}/g, ' ');
  return s.replace(/\u0000(\d+)\u0000/g, (_, i) => kept[Number(i)]);
}

// v1_841: an email field the model wrote a note into ("x@y.example
// (non-deliverable — reserved domain)"), split for display: the address as
// written, the rest as plain text. Display only; the stored value is unchanged.
export function emailAndNote(value) {
  const s = value == null ? '' : String(value).trim();
  const m = s.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/);
  if (!m) return { address: '', note: plainText(s) };
  const rest = (s.slice(0, m.index) + ' ' + s.slice(m.index + m[0].length)).trim()
    .replace(/^\((.*)\)$/s, '$1').trim();
  return { address: m[0], note: rest ? plainText(rest) : '' };
}

// Every string in a plain display object (arrays and objects walked), for a
// view model built only to render. Never for data that is written back.
export function plainDeep(value) {
  if (typeof value === 'string') return plainText(value);
  if (Array.isArray(value)) return value.map(plainDeep);
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = (typeof v === 'function') ? v : plainDeep(v);
    return out;
  }
  return value;
}
